/**
 * Typed search objects for the four section routes.
 *
 * Coerce every value, never type-guard it. TanStack's default search
 * parser runs JSON.parse on each raw value, so an all-digit id arrives
 * as a number. See `searchParams.ts` for the full rule.
 */
import {
  aliasLegacyPoolView,
  POOL_TABS,
} from "../features/poolScreen/poolScreenRoute";
import type { typeOverlaySearch } from "./overlay";
import {
  coerceOptionalEnum,
  coerceOptionalNumber,
  coerceOptionalString,
} from "./searchParams";

/** The one non-default view the Scores route can layer on. Absent means the games view. */
export const SCORES_VIEWS = ["standings"] as const;

/** The Lineup tab's team switcher (ISSUE-987). Absent means the away roster. */
export const LINEUP_TEAMS = ["away", "home"] as const;

export type typeScoresSearch = typeOverlaySearch & {
  leagueId?: string | undefined;
  date?: string | undefined;
  /** The selected season week on a league whose season runs in weeks
   *  (ISSUE-950). A NUMBER, unlike every id on this route, because a week
   *  number is a number (schema-typed-boundaries.mdc). It is also what keeps
   *  the shared link readable: TanStack stringifies search values with
   *  JSON.stringify, so an all-digit STRING would go out as `?week=%224%22`.
   *  The pane still checks it is a whole week before asking for it. */
  week?: number | undefined;
  /** The conference a college league's list is narrowed to (ISSUE-1387), a
   *  sports-api conferenceKey. Absent means every game. */
  conference?: string | undefined;
  view?: (typeof SCORES_VIEWS)[number] | undefined;
  grouping?: string | undefined;
  teamId?: string | undefined;
  eventId?: string | undefined;
  tab?: string | undefined;
  /** The Lineup tab's shown roster (ISSUE-987). Held here, not in a
   *  component's own `useState`, because a shared link, a hard refresh and
   *  browser back all have to land on the roster the fan was reading. Absent
   *  or an unknown value means the away roster. */
  lineupTeam?: (typeof LINEUP_TEAMS)[number] | undefined;
  /** The event id the share-to-a-chat picker sends. Set by the game detail
   *  header's Share action; read only by the share picker mounted once on
   *  this route. */
  shareEventId?: string | undefined;
  /** The event whose alerts dialog a score row's bell opened (ISSUE-979). Read only by the
   *  `GameAlertsDialog` mounted once on this route under `?sheet=row-alerts`. */
  alertsEventId?: string | undefined;
};

/** The five non-default views the Pools route can layer on. Absent means the pool's home.
 *
 * `member` is one member's pool-scoped profile (ISSUE-194), reached from a row
 * on `members`. It reuses the existing `memberId` key rather than minting a
 * second one. `memberId` is also the member-actions sheet target, but that is
 * read only under `view=members` and only alongside `sheet=member-actions`,
 * and `view` holds one value, so the two cannot be on screen at once. A second
 * key meaning "which member" would be the drift. */
export const POOLS_VIEWS = [
  "settings",
  "members",
  "notifications",
  "standings",
  "member",
] as const;

/**
 * The three source tabs inside the invite sheet. The sheet is an overlay over
 * the members view (`?sheet=invite`), not a fourth pools view, so this list is
 * separate from `POOLS_VIEWS`.
 */
export const INVITE_TABS = ["people", "chats", "pools"] as const;

export type typePoolsSearch = typeOverlaySearch & {
  poolId?: string | undefined;
  q?: string | undefined;
  joinCode?: string | undefined;
  view?: (typeof POOLS_VIEWS)[number] | undefined;
  panel?: string | undefined;
  memberId?: string | undefined;
  memberQ?: string | undefined;
  inviteTab?: (typeof INVITE_TABS)[number] | undefined;
  inviteQ?: string | undefined;
  entryId?: string | undefined;
  contestId?: string | undefined;
  /** The Game tab's selected round (ISSUE-973). Absent means the contest's current round. */
  roundId?: string | undefined;
  /**
   * The Strip Card Game tab's game (ISSUE-1292, ISSUE-1295). Absent means the
   * game the server picks. A Strip Card alert links here.
   */
  eventId?: string | undefined;
  /** The past seasons dialog's selected season (ISSUE-754). Absent means the list step. */
  seasonId?: string | undefined;
  /** Which of the pool screen's four tabs is open. Absent means Members. */
  tab?: (typeof POOL_TABS)[number] | undefined;
};

export type typeInvitesSearch = typeOverlaySearch & {
  inviteId?: string | undefined;
};

export type typeMessagesSearch = typeOverlaySearch & {
  conversationId?: string | undefined;
  messageId?: string | undefined;
  senderId?: string | undefined;
  /** The chat details panel's selected tab (WI-W10). Absent means `members`. */
  panel?: string | undefined;
  chatInvitationId?: string | undefined;
  /** ISSUE-1357. The Messages list search text. Absent means no filter. */
  q?: string | undefined;
  /** ISSUE-1362. The chat a list row's quick actions or mute dialog is about. */
  chatId?: string | undefined;
};

export type typeFanSearch = typeOverlaySearch & {
  accountId?: string | undefined;
};

export type typeAccountSearch = typeOverlaySearch & {
  awardGrantId?: string | undefined;
};

export function validateScoresSearch(
  search: Record<string, unknown>,
): typeScoresSearch {
  return {
    leagueId: coerceOptionalString(search.leagueId),
    date: coerceOptionalString(search.date),
    week: coerceOptionalNumber(search.week),
    conference: coerceOptionalString(search.conference),
    view: coerceOptionalEnum(search.view, SCORES_VIEWS),
    grouping: coerceOptionalString(search.grouping),
    teamId: coerceOptionalString(search.teamId),
    eventId: coerceOptionalString(search.eventId),
    tab: coerceOptionalString(search.tab),
    lineupTeam: coerceOptionalEnum(search.lineupTeam, LINEUP_TEAMS),
    shareEventId: coerceOptionalString(search.shareEventId),
    alertsEventId: coerceOptionalString(search.alertsEventId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

/**
 * `aliasLegacyPoolView` runs first, so a `?view=` link that is already out in
 * the world still resolves to the tab that replaced it (ISSUE-952 R5/R6).
 * `view=member` is not aliased: member detail is a drill-down over the
 * Members tab, not a fifth tab, and keeps its own address.
 */
export function validatePoolsSearch(
  raw: Record<string, unknown>,
): typePoolsSearch {
  const search = aliasLegacyPoolView(raw);
  return {
    poolId: coerceOptionalString(search.poolId),
    q: coerceOptionalString(search.q),
    joinCode: coerceOptionalString(search.joinCode),
    view: coerceOptionalEnum(search.view, POOLS_VIEWS),
    tab: coerceOptionalEnum(search.tab, POOL_TABS),
    panel: coerceOptionalString(search.panel),
    memberId: coerceOptionalString(search.memberId),
    memberQ: coerceOptionalString(search.memberQ),
    inviteTab: coerceOptionalEnum(search.inviteTab, INVITE_TABS),
    inviteQ: coerceOptionalString(search.inviteQ),
    entryId: coerceOptionalString(search.entryId),
    contestId: coerceOptionalString(search.contestId),
    roundId: coerceOptionalString(search.roundId),
    eventId: coerceOptionalString(search.eventId),
    seasonId: coerceOptionalString(search.seasonId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

export function validateInvitesSearch(
  search: Record<string, unknown>,
): typeInvitesSearch {
  return {
    inviteId: coerceOptionalString(search.inviteId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

export function validateMessagesSearch(
  search: Record<string, unknown>,
): typeMessagesSearch {
  return {
    conversationId: coerceOptionalString(search.conversationId),
    messageId: coerceOptionalString(search.messageId),
    senderId: coerceOptionalString(search.senderId),
    panel: coerceOptionalString(search.panel),
    chatInvitationId: coerceOptionalString(search.chatInvitationId),
    q: coerceOptionalString(search.q),
    chatId: coerceOptionalString(search.chatId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

export function validateFanSearch(
  search: Record<string, unknown>,
): typeFanSearch {
  return {
    accountId: coerceOptionalString(search.accountId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

export function validateAccountSearch(
  search: Record<string, unknown>,
): typeAccountSearch {
  return {
    awardGrantId: coerceOptionalString(search.awardGrantId),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

export type typeNotificationsSearch = typeOverlaySearch & {
  category?: string | undefined;
};

export function validateNotificationsSearch(
  search: Record<string, unknown>,
): typeNotificationsSearch {
  return {
    category: coerceOptionalString(search.category),
    sheet: coerceOptionalString(search.sheet),
    dialog: coerceOptionalString(search.dialog),
  };
}

/** The three tabs on `/account/followers` (ISSUE-1372), in display order. */
export const FOLLOWERS_TABS = ["requests", "followers", "following"] as const;
export type typeFollowersTab = (typeof FOLLOWERS_TABS)[number];

export type typeFollowersSearch = {
  /** Absent means "pick for me": Requests when any are waiting, else Followers. */
  tab?: typeFollowersTab | undefined;
};

export function validateFollowersSearch(
  search: Record<string, unknown>,
): typeFollowersSearch {
  return { tab: coerceOptionalEnum(search.tab, FOLLOWERS_TABS) };
}
